import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  ShieldAlert,
  Users,
  X,
  LifeBuoy,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ReportAdvisorDialog from '@/components/chat/ReportAdvisorDialog';
import ReportThread from '@/components/chat/ReportThread';
import SupportThread from '@/components/chat/SupportThread';
import ChatAvatar from '@/components/chat/ChatAvatar';
import ChatThread, { MESSAGE_POLL_MS } from '@/components/chat/ChatThread';
import { useChat } from '@/context/ChatProvider';
import { useOrgs } from '@/context/OrgsProvider';
import { fetchConversations, fetchReportOptions } from '@/data/advisor-chat';

function BackToConversations({ onBack, unread }: { onBack: () => void; unread: number }) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={onBack}
        aria-label={
          unread > 0
            ? `Back to your conversations, ${unread} unread in another thread`
            : 'Back to your conversations'
        }
        data-testid="chat-back"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      {unread > 0 && (
        <Badge
          className="h-5 min-w-[20px] justify-center px-1.5 text-[10px]"
          title="Unread messages in another conversation"
          data-testid="chat-back-unread"
        >
          {unread}
        </Badge>
      )}
    </div>
  );
}

export default function AdvisorChatPanel() {
  const { isOpen, closeChat, activeConversationId, setActiveConversationId, support, setSupport, supportUnread } = useChat();
  const { activeOrg } = useOrgs();
  const navigate = useNavigate();
  const [reporting, setReporting] = React.useState(false);
  const [openReportId, setOpenReportId] = React.useState<string | null>(null);
  const [listMode, setListMode] = React.useState(false);

  const conversations = useQuery({
    queryKey: ['chat-conversations', activeOrg?.id],
    queryFn: fetchConversations,
    enabled: isOpen,
    refetchInterval: isOpen ? MESSAGE_POLL_MS : false,
    retry: false,
  });

  const reports = useQuery({
    queryKey: ['advisor-report-options'],
    queryFn: fetchReportOptions,
    enabled: isOpen,
    refetchInterval: isOpen ? MESSAGE_POLL_MS * 3 : false,
    retry: false,
  });

  const list = conversations.data?.conversations ?? [];
  const roles = conversations.data?.roles ?? [];
  const myReports = reports.data?.reports ?? [];
  const openReport = myReports.find((row) => row.id === openReportId) ?? null;

  const hasChoice = list.length > 1;
  const live = list.find((row) => row.is_open) ?? list[0] ?? null;
  const selectedId = activeConversationId ?? live?.id ?? null;
  const selected = list.find((row) => row.id === selectedId) ?? null;
  const hasAdvisor = list.some((row) => row.your_side === 'customer');
  const canPick =
    !conversations.isLoading && !hasAdvisor && !roles.includes('advisor');
  const showingList = !support && !openReportId && (listMode || !selectedId);

  const totalUnread =
    list.reduce((n, row) => n + row.unread, 0)
    + myReports.reduce((n, row) => n + row.unread, 0)
    + supportUnread;
  const openThreadUnread = support
    ? supportUnread
    : openReportId
      ? openReport?.unread ?? 0
      : selected?.unread ?? 0;
  const unreadElsewhere = Math.max(totalUnread - openThreadUnread, 0);
  const advisorRow =
    (!showingList && selected?.your_side === 'customer' ? selected : null) ??
    list.find((row) => row.your_side === 'customer' && row.is_open) ??
    null;

  React.useEffect(() => {
    if (!isOpen) {
      setListMode(false);
      setOpenReportId(null);
      setSupport(null);
    }
  }, [isOpen, setSupport]);

  if (!isOpen) return null;

  return (
    <aside
      className="fixed right-0 top-0 z-40 flex h-screen w-full max-w-md flex-col border-l bg-background shadow-xl"
      data-testid="chat-panel"
    >
      <header className="flex items-center gap-2 border-b px-4 py-3">
        {support ? (
          <>
            <BackToConversations
              onBack={() => { setSupport(null); setListMode(true); }}
              unread={unreadElsewhere}
            />
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
              <LifeBuoy className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" data-testid="chat-counterparty">BitSwan support</p>
              <p className="truncate text-[11px] text-muted-foreground">{support.label ?? 'Questions about your account, servers and limits'}</p>
            </div>
          </>
        ) : openReportId ? (
          <BackToConversations
            onBack={() => { setOpenReportId(null); setListMode(true); }}
            unread={unreadElsewhere}
          />
        ) : !showingList && (hasChoice || unreadElsewhere > 0) ? (
          <BackToConversations
            onBack={() => setListMode(true)}
            unread={unreadElsewhere}
          />
        ) : (
          <MessageSquare className="h-4 w-4 shrink-0 text-amber-500" />
        )}

        {openReportId && openReport && (
          <ChatAvatar
            name={openReport.advisor_name}
            photoUrl={openReport.advisor_photo_url}
            className="h-9 w-9"
          />
        )}

        {!support && !openReportId && !showingList && selected && (
          <ChatAvatar
            name={selected.counterparty}
            photoUrl={selected.counterparty_photo_url}
            className="h-9 w-9"
          />
        )}

        {!support && <div className="min-w-0 flex-1">
          {openReportId ? (
            <>
              <p className="truncate text-sm font-medium" data-testid="chat-counterparty">
                BitSwan
              </p>
              <p className="truncate text-[11px] text-muted-foreground">
                About {openReport?.advisor_name ?? 'your report'}
              </p>
            </>
          ) : !showingList && selected?.counterparty_profile_id ? (
            <a
              href={`/public/advisor/${selected.counterparty_profile_id}`}
              target="_blank"
              rel="noreferrer"
              title={`See ${selected.counterparty}'s profile`}
              className="group flex items-center gap-1 text-sm font-medium hover:underline"
              data-testid="view-advisor-profile"
            >
              <span className="truncate" data-testid="chat-counterparty">
                {selected.counterparty}
              </span>
              <ExternalLink className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-70" />
            </a>
          ) : (
            <p className="truncate text-sm font-medium" data-testid="chat-counterparty">
              {showingList ? 'Your conversations' : selected?.counterparty ?? 'Messages'}
            </p>
          )}
          {!showingList && selected?.counterparty_headline && (
            <p className="truncate text-[11px] text-muted-foreground">
              {selected.counterparty_headline}
            </p>
          )}
        </div>}

        {!support && !openReportId && advisorRow && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="More about this advisor"
                data-testid="chat-advisor-menu"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => { closeChat(); navigate('/find-an-advisor'); }} data-testid="change-advisor-btn">
                <Users className="mr-2 h-4 w-4" />
                Switch advisors
              </DropdownMenuItem>
              {advisorRow.counterparty_profile_id && (
                <DropdownMenuItem
                  onClick={() => setReporting(true)}
                  className="text-red-600 focus:text-red-600"
                  data-testid="report-advisor-btn"
                >
                  <ShieldAlert className="mr-2 h-4 w-4" />
                  Report {advisorRow.counterparty} to BitSwan
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button variant="ghost" size="icon" onClick={closeChat} data-testid="chat-close">
          <X className="h-4 w-4" />
        </Button>
      </header>

      {showingList ? (
        <div className="flex-1 overflow-y-auto p-2" data-testid="chat-list">
          {conversations.isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {canPick && (
                <Link
                  to="/find-an-advisor"
                  onClick={closeChat}
                  className="mb-2 flex w-full items-center gap-3 rounded-md border border-dashed px-3 py-2.5 text-left hover:bg-muted"
                  data-testid="find-an-advisor"
                >
                  <MessageSquare className="h-5 w-5 shrink-0 text-amber-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">Find an automation advisor</p>
                    <p className="text-[11px] text-muted-foreground">
                      You do not have one yet. Read their profiles and pick
                      whoever looks right.
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Link>
              )}
              <button
                type="button"
                onClick={() => setSupport({})}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-muted"
                data-testid="chat-support"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                  <LifeBuoy className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">BitSwan support</p>
                  <p className="text-[11px] text-muted-foreground">Your account, servers and limits</p>
                </div>
                {supportUnread > 0 && (
                  <Badge className="h-5 min-w-[20px] justify-center px-1.5 text-[10px]">{supportUnread}</Badge>
                )}
              </button>
              {list.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => {
                    setActiveConversationId(row.id);
                    setListMode(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-muted"
                  data-testid={`chat-conversation-${row.id}`}
                >
                  <ChatAvatar name={row.counterparty} photoUrl={row.counterparty_photo_url} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{row.counterparty}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {row.last_message_at
                        ? new Date(row.last_message_at).toLocaleString()
                        : 'No messages yet'}
                      {!row.is_open && ' · closed'}
                    </p>
                  </div>
                  {row.unread > 0 && (
                    <Badge className="h-5 min-w-[20px] justify-center px-1.5 text-[10px]">
                      {row.unread}
                    </Badge>
                  )}
                </button>
              ))}

              {myReports.length > 0 && (
                <div className="mt-3" data-testid="report-list">
                  <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Your reports to BitSwan
                  </p>
                  {myReports.map((row) => (
                    <button
                      key={row.id}
                      type="button"
                      onClick={() => { setOpenReportId(row.id); setListMode(false); }}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left hover:bg-muted"
                      data-testid={`report-conversation-${row.id}`}
                    >
                      <ShieldAlert className="h-5 w-5 shrink-0 text-red-600" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          About {row.advisor_name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {row.status_display}
                          {row.last_message_at
                            ? ` · ${new Date(row.last_message_at).toLocaleString()}`
                            : ' · no reply yet'}
                        </p>
                      </div>
                      {row.unread > 0 && (
                        <Badge className="h-5 min-w-[20px] justify-center px-1.5 text-[10px]">
                          {row.unread}
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : support ? (
        <div className="min-h-0 flex-1">
          <SupportThread key={support.orgId ?? 'active'} orgId={support.orgId} initialDraft={support.draft} />
        </div>
      ) : openReportId ? (
        <div className="min-h-0 flex-1">
          <ReportThread key={openReportId} reportId={openReportId} />
        </div>
      ) : selectedId ? (
        <div className="min-h-0 flex-1">
          <ChatThread key={selectedId} conversationId={selectedId} />
        </div>
      ) : null}

      {reporting && selected?.counterparty_profile_id && (
        <ReportAdvisorDialog
          advisorProfileId={selected.counterparty_profile_id}
          advisorName={selected.counterparty}
          onClose={() => setReporting(false)}
        />
      )}

    </aside>
  );
}
